import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Combobox } from '@/components/ui/combobox';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';

interface TeamMemberFormData {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  role: string;
  password: string;
  confirmPassword: string;
}

interface TeamMemberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

const roles = [
  { value: 'ADMIN', label: 'Admin', description: 'Full access to all features' },
  { value: 'SALES_MANAGER', label: 'Sales Manager', description: 'Manage sales team and quotes' },
  { value: 'AGENT', label: 'Agent', description: 'Create bookings and quotes' },
  { value: 'ACCOUNTANT', label: 'Accountant', description: 'Access to invoices and payments' },
  { value: 'OPERATIONS', label: 'Operations', description: 'Manage packages and partners' },
  { value: 'VIEWER', label: 'Viewer', description: 'Read-only access' },
];

export function TeamMemberModal({ open, onOpenChange, onSuccess }: TeamMemberModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue,
  } = useForm<TeamMemberFormData>({
    defaultValues: {
      role: 'AGENT',
    },
  });

  const password = watch('password');

  const createMutation = trpc.settings.createTeamMember.useMutation({
    onSuccess: () => {
      reset();
      onOpenChange(false);
      onSuccess?.();
      showSuccessToast('Team member created successfully');
    },
    onError: (error) => {
      setIsSubmitting(false);
      showErrorToast(error.message || 'Failed to create team member');
    },
  });

  const onSubmit = (data: TeamMemberFormData) => {
    setIsSubmitting(true);
    createMutation.mutate({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      phone: data.phone,
      role: data.role as 'ADMIN' | 'SALES_MANAGER' | 'AGENT' | 'ACCOUNTANT' | 'OPERATIONS' | 'VIEWER',
      password: data.password,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add Team Member</DialogTitle>
          <DialogDescription>
            Create a new staff account with access to the admin panel.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name *</Label>
              <Input
                id="firstName"
                {...register('firstName', { required: 'First name is required' })}
                placeholder="John"
              />
              {errors.firstName && (
                <p className="text-sm text-red-500">{errors.firstName.message}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name *</Label>
              <Input
                id="lastName"
                {...register('lastName', { required: 'Last name is required' })}
                placeholder="Doe"
              />
              {errors.lastName && (
                <p className="text-sm text-red-500">{errors.lastName.message}</p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              placeholder="john.doe@company.com"
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input
              id="phone"
              {...register('phone')}
              placeholder="+1 234 567 8900"
            />
          </div>

          <div className="space-y-2">
            <Label>Role *</Label>
            <Combobox
              options={roles.map((role) => ({
                value: role.value,
                label: `${role.label} - ${role.description}`,
              }))}
              value={watch('role')}
              onValueChange={(value) => setValue('role', value)}
              placeholder="Select role"
              searchPlaceholder="Search roles..."
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Password *</Label>
            <Input
              id="password"
              type="password"
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 8,
                  message: 'Password must be at least 8 characters',
                },
              })}
              placeholder="••••••••"
            />
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword">Confirm Password *</Label>
            <Input
              id="confirmPassword"
              type="password"
              {...register('confirmPassword', {
                required: 'Please confirm password',
                validate: (value) =>
                  value === password || 'Passwords do not match',
              })}
              placeholder="••••••••"
            />
            {errors.confirmPassword && (
              <p className="text-sm text-red-500">{errors.confirmPassword.message}</p>
            )}
          </div>

          {createMutation.error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
              {createMutation.error.message}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create Member
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
